/**
 * PublicDesignView.jsx — Read-only viewer for a published Design
 * Studio drawing. Hits /api/public/design/{token} (no auth) and renders
 * the raster + SVG using the same fitSvgToContainer normaliser as the
 * authenticated Design Studio, but WITHOUT markup / refine / history.
 *
 * Anyone with the URL can view. There's no login prompt, no tenant
 * chrome, no CTA — the goal is a "share with a client for review"
 * surface that feels clean and standalone.
 */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Download, ExternalLink, Sparkles } from 'lucide-react';
import { designApi } from '../services/drawings';

// Same helper as the authenticated studio — strips fixed width/height
// so the SVG scales to fit any container.
const fitSvgToContainer = (svg) => {
  if (!svg || typeof svg !== 'string') return svg;
  return svg.replace(/<svg\b([^>]*)>/i, (_, attrs) => {
    let cleaned = attrs
      .replace(/\swidth\s*=\s*(['"]).*?\1/i, '')
      .replace(/\sheight\s*=\s*(['"]).*?\1/i, '');
    if (!/preserveAspectRatio/i.test(cleaned)) cleaned += ' preserveAspectRatio="xMidYMid meet"';
    return `<svg${cleaned} width="100%" height="100%" style="max-width:100%;max-height:100%;height:auto;width:100%;display:block">`;
  });
};

export default function PublicDesignView() {
  const { token } = useParams();
  const [gen, setGen] = useState(null);
  const [tab, setTab] = useState('svg');
  const [status, setStatus] = useState('loading');   // 'loading' | 'ready' | 'notfound'

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await designApi.getPublic(token);
        if (!cancelled) {
          setGen(data);
          setStatus('ready');
          if (!data.svg && data.raster_b64) setTab('raster');
        }
      } catch (e) {
        if (!cancelled) setStatus('notfound');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--layer-0)' }}>
        <Loader2 className="animate-spin" size={20} style={{ color: '#F26B1F' }} />
      </div>
    );
  }
  if (status === 'notfound') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--layer-0)' }}>
        <div className="text-center" data-testid="public-notfound">
          <p className="text-nexus-text-primary text-lg mb-2">Design not found or no longer public</p>
          <Link to="/" className="text-[13px]" style={{ color: '#F26B1F' }}>← Back to Cognara</Link>
        </div>
      </div>
    );
  }

  const rawSvg = gen?.svg || '';
  const svgToShow = fitSvgToContainer(rawSvg);
  const rasterUrl = gen?.raster_b64 ? `data:image/png;base64,${gen.raster_b64}` : null;
  const hasSvg = !!svgToShow;
  const hasRaster = !!rasterUrl;

  const download = () => {
    if (tab === 'svg' && rawSvg) {
      const blob = new Blob([rawSvg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `cognara-shared.svg`; a.click();
      URL.revokeObjectURL(url);
    } else if (tab === 'raster' && rasterUrl) {
      const a = document.createElement('a'); a.href = rasterUrl; a.download = 'cognara-shared.png'; a.click();
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--layer-0)' }} data-testid="public-design-view">
      <header className="border-b flex items-center justify-between px-6 py-3"
        style={{ borderColor: 'var(--hairline)', background: 'var(--layer-0)' }}>
        <div className="flex items-center gap-2">
          <Sparkles size={16} style={{ color: '#F26B1F' }} />
          <span className="text-[14px] font-bold text-nexus-text-primary">Cognara Design</span>
          <span className="text-[11px] text-nexus-text-tertiary hidden sm:inline">· Shared drawing</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={download}
            className="px-3 py-1.5 rounded-md text-[11.5px] font-semibold flex items-center gap-1.5"
            style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)', color: 'var(--text-primary)' }}
            data-testid="public-download">
            <Download size={12} /> Download
          </button>
          <a href="/" className="text-[11.5px] flex items-center gap-1" style={{ color: '#F26B1F' }}>
            Get Cognara <ExternalLink size={11} />
          </a>
        </div>
      </header>

      <main className="flex-1 p-4 sm:p-8 flex flex-col">
        {/* Prompt */}
        {gen?.prompt && (
          <div className="mb-4 rounded-lg p-3 max-w-4xl mx-auto w-full"
            style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
            <div className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary mb-1 font-semibold">Brief</div>
            <p className="text-[13px] text-nexus-text-primary whitespace-pre-wrap">{gen.prompt}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-3 max-w-4xl mx-auto w-full">
          {hasSvg && (
            <button onClick={() => setTab('svg')}
              className="px-3 py-1.5 rounded-md text-[11.5px] font-semibold"
              style={{
                background: tab === 'svg' ? '#F26B1F' : 'var(--layer-1)',
                color: tab === 'svg' ? '#fff' : 'var(--text-primary)',
              }}
              data-testid="public-tab-svg">SVG</button>
          )}
          {hasRaster && (
            <button onClick={() => setTab('raster')}
              className="px-3 py-1.5 rounded-md text-[11.5px] font-semibold"
              style={{
                background: tab === 'raster' ? '#F26B1F' : 'var(--layer-1)',
                color: tab === 'raster' ? '#fff' : 'var(--text-primary)',
              }}
              data-testid="public-tab-raster">Raster</button>
          )}
        </div>

        <div className="flex-1 rounded-lg overflow-auto flex items-center justify-center p-4 max-w-4xl mx-auto w-full"
          style={{ background: '#FFFFFF', border: '1px solid var(--hairline)', minHeight: 400 }}>
          {tab === 'svg' && hasSvg ? (
            <div dangerouslySetInnerHTML={{ __html: svgToShow }}
              className="w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg]:max-w-full [&>svg]:max-h-full" />
          ) : hasRaster ? (
            <img src={rasterUrl} alt="Shared drawing" className="max-w-full max-h-full object-contain" />
          ) : (
            <p className="text-nexus-text-tertiary text-sm">This design has no drawings yet.</p>
          )}
        </div>

        {gen?.published_at && (
          <p className="text-[10px] text-nexus-text-tertiary text-center mt-3">
            Published {new Date(gen.published_at).toLocaleString()}
            {gen.published_by_email ? ` by ${gen.published_by_email}` : ''} · Powered by Cognara
          </p>
        )}
      </main>
    </div>
  );
}
